/*
  foodora eShop – predictive search
  <fd-predictive-search> wraps the search form (snippets/search-form.liquid) and
  a results region. Requests sections/predictive-search.liquid through the
  Section Rendering API, debounced and abortable. Results are plain links, so
  they work with Tab; ArrowDown/ArrowUp move between them as a shortcut.
*/
(function () {
  'use strict';

  var FD = window.FD || {};
  var announce = FD.announce || function () {};
  var debounce = FD.debounce || function (fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  };
  var MIN_CHARS = 2;

  if (customElements.get('fd-predictive-search')) return;

  class FdPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-fd-ps-results]');
      if (!this.input || !this.results) return;

      var routes = (FD.config && FD.config.routes) || {};
      this.endpoint = routes.predictiveSearch || '/search/suggest';
      this.types = this.getAttribute('data-types') || 'product,collection';
      this.limit = this.getAttribute('data-limit') || '4';
      this.msgCount = this.getAttribute('data-msg-count') || '';
      this.msgError = this.getAttribute('data-msg-error') || '';
      this.cache = {};
      this.controller = null;
      this.lastQuery = '';

      this._run = debounce(this._search.bind(this), 250);
      this.input.addEventListener('input', this._run);
      this.input.addEventListener('keydown', this._onInputKey.bind(this));
      this.results.addEventListener('keydown', this._onResultsKey.bind(this));

      // Abort in-flight work when the dialog closes.
      var dialog = this.closest('dialog');
      if (dialog) dialog.addEventListener('close', this._abort.bind(this));
    }

    _abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.results.setAttribute('aria-busy', 'false');
    }

    _clear() {
      this._abort();
      this.lastQuery = '';
      this.results.textContent = '';
    }

    _search() {
      var query = this.input.value.trim();
      if (query.length < MIN_CHARS) {
        this._clear();
        return;
      }
      if (query === this.lastQuery) return;
      this.lastQuery = query;

      if (this.cache[query] != null) {
        this._render(this.cache[query]);
        return;
      }

      this._abort();
      var controller = (this.controller = new AbortController());
      this.results.setAttribute('aria-busy', 'true');

      var params = new URLSearchParams();
      params.set('q', query);
      params.set('section_id', 'predictive-search');
      params.set('resources[type]', this.types);
      params.set('resources[limit]', this.limit);
      params.set('resources[options][unavailable_products]', 'last');

      var self = this;
      fetch(this.endpoint + '?' + params.toString(), {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var root = doc.querySelector('[data-fd-ps]');
          var markup = root ? root.innerHTML : '';
          var count = root ? parseInt(root.getAttribute('data-count'), 10) || 0 : 0;
          var entry = { html: markup, count: count, empty: root ? root.querySelector('.fd-ps__empty') : null };
          entry.emptyText = entry.empty ? entry.empty.textContent : '';
          entry.empty = !!entry.empty;
          self.cache[query] = entry;
          if (self.controller === controller) self._render(entry);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self.lastQuery = '';
          self.results.setAttribute('aria-busy', 'false');
          self.results.textContent = '';
          var p = document.createElement('p');
          p.className = 'fd-ps__empty';
          p.textContent = self.msgError;
          self.results.appendChild(p);
          announce(self.msgError);
        });
    }

    // `entry.html` is server-rendered, escaped Liquid output from our own origin.
    _render(entry) {
      this.results.innerHTML = entry.html;
      this.results.setAttribute('aria-busy', 'false');
      if (entry.empty) announce(entry.emptyText);
      else if (this.msgCount) announce(this.msgCount.replace('__N__', String(entry.count)));
    }

    _links() {
      return Array.prototype.slice.call(this.results.querySelectorAll('a'));
    }

    _onInputKey(e) {
      if (e.key === 'ArrowDown') {
        var links = this._links();
        if (links.length) {
          e.preventDefault();
          links[0].focus();
        }
      }
    }

    _onResultsKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var links = this._links();
      var i = links.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        if (i < links.length - 1) links[i + 1].focus();
      } else if (i > 0) {
        links[i - 1].focus();
      } else {
        this.input.focus();
      }
    }
  }

  customElements.define('fd-predictive-search', FdPredictiveSearch);
})();
